/**
 * DENET — koleksiyon sayfası
 * Filtre ve sıralama sayfayı yenilemeden çalışır: Section Rendering API ile
 * yalnızca bölüm yeniden çizilir, URL geçmişe yazılır (geri tuşu çalışır).
 * "Daha fazla göster" sonuçları mevcut ızgaranın sonuna ekler.
 */

import { DenetElement, define, debounce, qsa, lockScroll, unlockScroll } from '@denet/core';
import { withTransition } from '@denet/transitions';

/* Liste durumu (kaç sayfa yüklendi, hangi ürüne tıklandı) sekme boyunca saklanır:
   ürüne girip geri dönen müşteri listenin başına düşmesin. */
const listeAnahtari = () => {
  const p = new URLSearchParams(window.location.search);
  p.delete('page');
  return `denet:liste:${window.location.pathname}?${p}`;
};
const listeOku = () => { try { return JSON.parse(sessionStorage.getItem(listeAnahtari()) || 'null'); } catch { return null; } };
const listeYaz = (v) => { try { sessionStorage.setItem(listeAnahtari(), JSON.stringify(v)); } catch { /* gizli sekme */ } };

class DnCollection extends DenetElement {
  mounted() {
    this.sectionId = this.dataset.section;
    this.grid = this.ref('grid');

    for (const form of qsa('[data-ref="facetForm"], [data-ref="facetFormMobile"]', this)) {
      form.addEventListener('change', debounce(() => this.#applyForm(form), 300), { signal: this.signal });
      // Enter ile form gönderimi sayfayı yenilemesin.
      form.addEventListener('submit', (event) => { event.preventDefault(); this.#applyForm(form); }, { signal: this.signal });
    }

    /* Alt kategori kutucuklarında gizli kalanları açar. Sunucudan hepsi
       geliyor, yalnız fazlası hidden — böylece JS çalışmasa da bağlantılar
       sayfada duruyor ve arama motoru hepsini görüyor. */
    this.ref('moreCats')?.addEventListener('click', (event) => {
      for (const li of qsa('.dn-subcats__extra', this)) li.hidden = false;
      /** @type {HTMLElement} */ (event.currentTarget).closest('li')?.remove();
    }, { signal: this.signal });

    this.ref('sort')?.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', /** @type {HTMLSelectElement} */ (event.target).value);
      url.searchParams.delete('page');
      this.#render(url.toString());
    }, { signal: this.signal });

    this.addEventListener('click', (event) => {
      const remove = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-remove]') : null);
      if (remove?.dataset.remove) {
        event.preventDefault();
        this.#render(remove.dataset.remove);
        return;
      }

      const more = /** @type {HTMLAnchorElement | null} */ (event.target instanceof Element ? event.target.closest('[data-ref="more"]') : null);
      if (more) { event.preventDefault(); this.#loadMore(more.href); }
    }, { signal: this.signal });

    /* Ürüne tıklanınca nerede kalındığı kaydedilir. */
    this.addEventListener('click', (event) => {
      const bag = event.target instanceof Element ? event.target.closest('.dn-pcard a[href*="/products/"]') : null;
      if (!bag) return;
      const sayfa = Number(new URLSearchParams(window.location.search).get('page') || 1);
      listeYaz({ sayfa: Math.max(sayfa, listeOku()?.sayfa || 1), yol: new URL(bag.href).pathname, y: window.scrollY });
    }, { signal: this.signal, capture: true });

    this.#initDrawer();
    if (!this.geriYuklendi) { this.geriYuklendi = true; this.#geriDon(); }
  }

  /**
   * Geri tuşuyla dönüldüyse liste kaldığı hâline getirilir: daha önce yüklenen
   * sayfalar yeniden çekilir, tıklanan ürünün kartı ekrana getirilir.
   * Tarayıcı sayfayı belleğinden (bfcache) geri getirdiyse buna gerek kalmaz.
   */
  async #geriDon() {
    const gezinme = performance.getEntriesByType('navigation')[0];
    const kayit = listeOku();
    if (!kayit || !this.grid || !gezinme || gezinme.type !== 'back_forward') return;

    const bul = () => (kayit.yol ? this.grid.querySelector(`.dn-pcard a[href$="${CSS.escape(kayit.yol.split('/').pop())}"]`) : null);
    const mevcut = Number(new URLSearchParams(window.location.search).get('page') || 1);
    /* Adreste yalnız son sayfa durur (?page=3); önceki sayfalar yeniden çekilir. */
    const eksik = kayit.sayfa > 1 || mevcut > 1;

    if (eksik) {
      window.history.scrollRestoration = 'manual';
      this.grid.dataset.loading = 'true';
      try {
        const taban = new URL(window.location.href);
        const sayfalar = await Promise.all(Array.from({ length: Math.max(kayit.sayfa, mevcut) }, (_, i) => {
          taban.searchParams.set('page', String(i + 1));
          return fetch(`${taban}&section_id=${this.sectionId}`).then((r) => (r.ok ? r.text() : ''));
        }));
        const parca = document.createDocumentFragment();
        let son = null;
        for (const html of sayfalar) {
          if (!html) continue;
          son = new DOMParser().parseFromString(html, 'text/html');
          for (const kart of son.querySelectorAll('[data-ref="grid"] > *')) parca.appendChild(kart);
        }
        if (parca.childElementCount) {
          this.grid.replaceChildren(parca);
          const dugme = this.ref('more');
          const yeniDugme = son?.querySelector('[data-ref="more"]');
          if (dugme && yeniDugme instanceof HTMLAnchorElement) dugme.setAttribute('href', yeniDugme.href);
          else dugme?.remove();
          const bilgi = son?.querySelector('.dn-cmore__info');
          const cubuk = son?.querySelector('.dn-cmore__bar span');
          if (bilgi) this.querySelector('.dn-cmore__info')?.replaceWith(bilgi);
          if (cubuk) this.querySelector('.dn-cmore__bar span')?.replaceWith(cubuk);
        }
      } catch { /* olmadıysa sayfa olduğu gibi kalır */ }
      delete this.grid.dataset.loading;
    }

    /* Konum tarayıcıya bırakılmaz: o, sayfa yüklenince kendi hatırladığı
       (çoğu kez en üst) konuma döner ve bizim kaydırmamızı ezer. Kaydırma
       yerleşim oturduktan sonra ve sayfa tam yüklenince bir kez daha yapılır. */
    const git = () => {
      const kart = bul()?.closest('.dn-pcard');
      if (kart) kart.scrollIntoView({ block: 'center', behavior: 'instant' });
      else if (kayit.y) window.scrollTo({ top: kayit.y, behavior: 'instant' });
    };
    window.history.scrollRestoration = 'manual';
    requestAnimationFrame(() => requestAnimationFrame(git));
    if (document.readyState !== 'complete') window.addEventListener('load', () => window.setTimeout(git, 60), { once: true });
  }

  /** @param {HTMLFormElement} form */
  #applyForm(form) {
    const params = new URLSearchParams(new FormData(form));
    // Boş değerler URL'i kirletmesin.
    for (const [key, value] of [...params]) if (!value) params.delete(key);
    params.delete('page');
    this.#render(`${window.location.pathname}?${params}`);
  }

  /** @param {string} url */
  async #render(url) {
    if (this.grid) this.grid.dataset.loading = 'true';

    try {
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(String(response.status));

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const next = parsed.querySelector('dn-collection');
      if (!next) throw new Error('section missing');

      /* Izgara tek hamlede değil, yumuşak bir geçişle yenilenir. */
      await withTransition(() => {
        this.innerHTML = next.innerHTML;
        this.mounted();
      });
      window.history.pushState({}, '', url);
      listeYaz({ sayfa: 1 });
      /* Liste zaten ekrandaysa sayfa oynatılmaz; yalnız üstü kaçmışsa geri getirilir. */
      if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } catch {
      // Ağ hatasında normal gezinmeye düş: kullanıcı sonucu yine görür.
      window.location.href = url;
    }
  }

  /** @param {string} url */
  async #loadMore(url) {
    const button = this.ref('more');
    if (!button || !this.grid) return;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${url}&section_id=${this.sectionId}`);
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');

      const cards = parsed.querySelectorAll('[data-ref="grid"] > *');
      for (const card of cards) this.grid.appendChild(card);

      const nextMore = parsed.querySelector('[data-ref="more"]');
      const info = parsed.querySelector('.dn-cmore__info');
      const bar = parsed.querySelector('.dn-cmore__bar span');

      if (info) this.querySelector('.dn-cmore__info')?.replaceWith(info);
      if (bar) this.querySelector('.dn-cmore__bar span')?.replaceWith(bar);

      if (nextMore instanceof HTMLAnchorElement) button.setAttribute('href', nextMore.href);
      else button.remove();

      window.history.replaceState({}, '', url);
      listeYaz({ ...(listeOku() || {}), sayfa: Number(new URL(url, window.location.href).searchParams.get('page') || 1) });
    } catch {
      window.location.href = url;
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  #initDrawer() {
    const drawer = /** @type {HTMLDialogElement | null} */ (this.ref('facetDrawer'));
    if (!drawer) return;

    this.ref('facetOpen')?.addEventListener('click', () => { drawer.showModal(); lockScroll(); }, { signal: this.signal });
    this.ref('facetClose')?.addEventListener('click', () => drawer.close(), { signal: this.signal });
    this.ref('facetApply')?.addEventListener('click', () => drawer.close(), { signal: this.signal });
    drawer.addEventListener('close', () => unlockScroll(), { signal: this.signal });
    drawer.addEventListener('click', (event) => { if (event.target === drawer) drawer.close(); }, { signal: this.signal });
  }
}

define('dn-collection', DnCollection);
